import { lazy, Suspense, ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { RedirectIfAuth } from './RedirectIfAuth';
import { FullPageSpinner } from '../components/ui/Spinner';

/**
 * Adapte React.lazy aux pages exportées en named export (convention du projet).
 * Garde une seule entrée par route → un seul chunk distinct par page côté webpack.
 */
const lazyNamed = <K extends string>(
  loader: () => Promise<Record<K, ComponentType<unknown>>>,
  name: K,
) => lazy(() => loader().then((m) => ({ default: m[name] })));

const Landing = lazyNamed(() => import('../pages/public/Landing'), 'Landing');
const EventsList = lazyNamed(() => import('../pages/public/EventsList'), 'EventsList');
const EventsCalendar = lazyNamed(() => import('../pages/public/EventsCalendar'), 'EventsCalendar');
const EventDetail = lazyNamed(() => import('../pages/public/EventDetail'), 'EventDetail');
const IccDashboard = lazyNamed(() => import('../pages/public/IccDashboard'), 'IccDashboard');
const Observatoire = lazyNamed(() => import('../pages/public/Observatoire'), 'Observatoire');
const EventSurvey = lazyNamed(() => import('../pages/public/EventSurvey'), 'EventSurvey');
const Campaigns = lazyNamed(() => import('../pages/public/Campaigns'), 'Campaigns');
const CampaignDetail = lazyNamed(() => import('../pages/public/CampaignDetail'), 'CampaignDetail');
const Credits = lazyNamed(() => import('../pages/public/Credits'), 'Credits');
const About = lazyNamed(() => import('../pages/public/About'), 'About');
const LegalNotice = lazyNamed(() => import('../pages/public/LegalNotice'), 'LegalNotice');
const CollaborationTerms = lazyNamed(
  () => import('../pages/public/CollaborationTerms'),
  'CollaborationTerms',
);
const Privacy = lazyNamed(() => import('../pages/public/Privacy'), 'Privacy');
const Contact = lazyNamed(() => import('../pages/public/Contact'), 'Contact');
const DownloadApp = lazyNamed(() => import('../pages/public/DownloadApp'), 'DownloadApp');
const ContestApply = lazyNamed(() => import('../pages/public/ContestApply'), 'ContestApply');
const NomineeVote = lazyNamed(() => import('../pages/public/NomineeVote'), 'NomineeVote');
const ContestsRanking = lazyNamed(
  () => import('../pages/public/ContestsRanking'),
  'ContestsRanking',
);
const NotFound = lazyNamed(() => import('../pages/public/NotFound'), 'NotFound');

// Styleguide vivant du socle SP-0 — monté uniquement hors production.
const Styleguide = lazyNamed(() => import('../pages/dev/Styleguide'), 'Styleguide');

const Login = lazyNamed(() => import('../pages/auth/Login'), 'Login');
const Register = lazyNamed(() => import('../pages/auth/Register'), 'Register');
const TwoFactor = lazyNamed(() => import('../pages/auth/TwoFactor'), 'TwoFactor');
const ForgotPassword = lazyNamed(() => import('../pages/auth/ForgotPassword'), 'ForgotPassword');
const VerifyEmail = lazyNamed(() => import('../pages/auth/VerifyEmail'), 'VerifyEmail');
const ResetPassword = lazyNamed(() => import('../pages/auth/ResetPassword'), 'ResetPassword');

const Checkout = lazyNamed(() => import('../pages/checkout/Checkout'), 'Checkout');
const CheckoutWaiting = lazyNamed(
  () => import('../pages/checkout/CheckoutWaiting'),
  'CheckoutWaiting',
);
const CheckoutSuccess = lazyNamed(
  () => import('../pages/checkout/CheckoutSuccess'),
  'CheckoutSuccess',
);

const MyTickets = lazyNamed(() => import('../pages/account/MyTickets'), 'MyTickets');
const Wallet = lazyNamed(() => import('../pages/account/Wallet'), 'Wallet');
const Profile = lazyNamed(() => import('../pages/account/Profile'), 'Profile');
const BecomeOrganizer = lazyNamed(
  () => import('../pages/account/BecomeOrganizer'),
  'BecomeOrganizer',
);

const OrganizerDashboard = lazyNamed(
  () => import('../pages/organizer/Dashboard'),
  'OrganizerDashboard',
);
const EventsManage = lazyNamed(() => import('../pages/organizer/EventsManage'), 'EventsManage');
const EventNew = lazyNamed(() => import('../pages/organizer/EventNew'), 'EventNew');
const EventEdit = lazyNamed(() => import('../pages/organizer/EventEdit'), 'EventEdit');
const Analytics = lazyNamed(() => import('../pages/organizer/Analytics'), 'Analytics');
const OrganizerCampaigns = lazyNamed(
  () => import('../pages/organizer/Campaigns'),
  'OrganizerCampaigns',
);
const CampaignNew = lazyNamed(() => import('../pages/organizer/CampaignNew'), 'CampaignNew');
const CampaignEdit = lazyNamed(() => import('../pages/organizer/CampaignEdit'), 'CampaignEdit');
const EventContest = lazyNamed(() => import('../pages/organizer/EventContest'), 'EventContest');
const OrganizerEventSurvey = lazyNamed(
  () => import('../pages/organizer/EventSurvey'),
  'EventSurvey',
);
const OrganizerWallet = lazyNamed(() => import('../pages/organizer/Wallet'), 'OrganizerWallet');
const CancellationFees = lazyNamed(
  () => import('../pages/organizer/CancellationFees'),
  'CancellationFees',
);

const AdminOverviewPage = lazyNamed(() => import('../pages/admin/Overview'), 'AdminOverviewPage');
const AdminEventModeration = lazyNamed(
  () => import('../pages/admin/EventModeration'),
  'EventModeration',
);
const KycModeration = lazyNamed(() => import('../pages/admin/KycModeration'), 'KycModeration');
const AdminUsers = lazyNamed(() => import('../pages/admin/Users'), 'AdminUsers');
const AdminEvents = lazyNamed(() => import('../pages/admin/Events'), 'AdminEvents');
const AdminCampaigns = lazyNamed(() => import('../pages/admin/Campaigns'), 'AdminCampaigns');
const AdminComments = lazyNamed(() => import('../pages/admin/Comments'), 'AdminComments');
const AdminSurveyResponses = lazyNamed(
  () => import('../pages/admin/SurveyResponses'),
  'AdminSurveyResponses',
);
const AdminContent = lazyNamed(() => import('../pages/admin/Content'), 'AdminContent');
const AdminTransactions = lazyNamed(
  () => import('../pages/admin/Transactions'),
  'AdminTransactions',
);
const AdminFraudLogs = lazyNamed(() => import('../pages/admin/FraudLogs'), 'AdminFraudLogs');
const AdminWithdrawals = lazyNamed(() => import('../pages/admin/Withdrawals'), 'AdminWithdrawals');
const AdminCancellationFees = lazyNamed(
  () => import('../pages/admin/CancellationFees'),
  'AdminCancellationFees',
);

export const AppRoutes = () => (
  <Suspense fallback={<FullPageSpinner />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/events" element={<EventsList />} />
      <Route path="/calendrier" element={<EventsCalendar />} />
      <Route path="/events/:id" element={<EventDetail />} />
      <Route path="/events/:id/avis" element={<EventSurvey />} />
      <Route path="/financement" element={<Campaigns />} />
      <Route path="/financement/:id" element={<CampaignDetail />} />
      <Route path="/icc" element={<IccDashboard />} />
      <Route path="/observatoire" element={<Observatoire />} />
      <Route path="/credits" element={<Credits />} />
      <Route path="/a-propos" element={<About />} />
      <Route path="/mentions-legales" element={<LegalNotice />} />
      <Route path="/conditions-collaboration" element={<CollaborationTerms />} />
      <Route path="/confidentialite" element={<Privacy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/app-mobile" element={<DownloadApp />} />
      {/* Page ouverte par le lien / QR code de candidature à un concours — pas
          de ProtectedRoute : un visiteur non connecté doit pouvoir consulter
          le formulaire (comportement 1 du brief), la connexion n'étant
          exigée qu'au moment du dépôt. */}
      <Route path="/candidature/:token" element={<ContestApply />} />
      {/* Page ouverte par le lien / QR code de vote d'UN candidat — publique :
          le vote payant ne demande aucun compte, le vote gratuit propose la
          connexion sur place. */}
      <Route path="/vote/:nomineeId" element={<NomineeVote />} />
      <Route path="/concours/classement" element={<ContestsRanking />} />
      {process.env.NODE_ENV !== 'production' && (
        <Route path="/styleguide" element={<Styleguide />} />
      )}

      {/* Auth — bloqué si déjà connecté */}
      <Route
        path="/login"
        element={
          <RedirectIfAuth>
            <Login />
          </RedirectIfAuth>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfAuth>
            <Register />
          </RedirectIfAuth>
        }
      />
      <Route
        path="/2fa"
        element={
          <RedirectIfAuth>
            <TwoFactor />
          </RedirectIfAuth>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <RedirectIfAuth>
            <ForgotPassword />
          </RedirectIfAuth>
        }
      />
      {/* Pas de RedirectIfAuth : l'inscription connecte déjà l'utilisateur,
          qui doit pouvoir ouvrir ce lien juste après s'être inscrit. */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        path="/reset-password"
        element={
          <RedirectIfAuth>
            <ResetPassword />
          </RedirectIfAuth>
        }
      />

      {/* /checkout (achat de billet) : exige toujours l'authentification côté
          backend (POST /payments/intent protégé) — reste derrière
          ProtectedRoute → redirection /login puis retour ici. */}
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        }
      />
      {/* /checkout/waiting : PAS de ProtectedRoute — le vote payant (concours)
          n'exige plus de compte (retour testeurs), et cette page d'attente
          sert aussi bien à un paiement de billet (déjà passé par /checkout,
          donc déjà authentifié à ce stade) qu'à un paiement de vote anonyme.
          GET /payments/:ref est déjà accessible sans compte côté backend
          (optionalAuth — la référence, non devinable, joue le rôle de clé). */}
      <Route path="/checkout/waiting" element={<CheckoutWaiting />} />
      {/* Succès : aucune requête protégée, reste accessible (lien de confirmation). */}
      <Route path="/checkout/success" element={<CheckoutSuccess />} />

      {/* Espace utilisateur */}
      <Route
        path="/tickets"
        element={
          <ProtectedRoute>
            <MyTickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/wallet"
        element={
          <ProtectedRoute>
            <Wallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/become-organizer"
        element={
          <ProtectedRoute>
            <BecomeOrganizer />
          </ProtectedRoute>
        }
      />

      {/* Organisateur — exige KYC APPROVED (sauf ADMIN) */}
      <Route
        path="/organizer/dashboard"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <OrganizerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/events"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <EventsManage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/events/new"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <EventNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/events/:id/edit"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <EventEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/events/:id/contest"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <EventContest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/events/:id/satisfaction"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <OrganizerEventSurvey />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/campaigns"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <OrganizerCampaigns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/campaigns/new"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <CampaignNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/campaigns/:id/edit"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <CampaignEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/analytics"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/wallet"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <OrganizerWallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/cancellation-fees"
        element={
          <ProtectedRoute roles={['ORGANIZER', 'ADMIN']} kycApproved>
            <CancellationFees />
          </ProtectedRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <Navigate to="/admin/overview" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/overview"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kyc"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <KycModeration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/event-moderation"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminEventModeration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminCampaigns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/comments"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminComments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/survey-responses"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminSurveyResponses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminContent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transactions"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminTransactions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fraud-logs"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminFraudLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/withdrawals"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminWithdrawals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cancellation-fees"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminCancellationFees />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);
